// Core
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { NavLink, useLocation} from 'react-router-dom';
import {
  AbsoluteCenter,
  Box,
  Flex,
  Button,
  Divider,
  Grid,
  Image,
  Link,
  Text,
  useDisclosure,
  useColorModeValue,
  useColorMode
} from '@chakra-ui/react';

// Assets
import logoImage from 'assets/img/logo.png';
import headerImage from 'assets/img/header-sign-up.png';

// Components
import SignUp from './SignUp';
import SignUpFinish from './SignUpFinish';
import { 
  StarsIcon,  
  SignUpGamesIcon,
  SignUpMembershipIcon,
  SignUpPetrolIcon
} from 'components/Icons/Icons';
import Card from 'components/Card/Card';

function SelectSignUp({step, setStep}) {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const topLineDark = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 56.18%, #FFF 68.16%, #38A9E1 82.69%, rgba(56, 169, 225, 0.00) 100%)';
  const topLineWhite = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #38A9E1 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
  const topLine = useColorModeValue(topLineWhite, topLineDark);
  const spanStyle = {
    color: '#45B4EA',
    fontWeight: 700
  };

  const handleType = (type) => {
    switch (type) {
      case 'free':
        setStep(2);
        break;
      case 'membership':
        window.location.href = 'https://lmctgiveaway.com/loyalty-membership';
        break;
      case 'petrol':
        window.location.href = 'https://lmctgiveaway.com/petrol-loyalty';
        break;
      default:
        break;
    }
  };

  const [newObject, setNewObject] = useState({});
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <>
    { step === 1 &&
      <Box w='100%' 
        textAlign="center" 
        overflow="scroll" 
        sx={{
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none', 
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        h="100%" 
        py="80px"
        zIndex="3">
        <Box position="relative" mb={{base: '50px', sm: '60px', md: '79px'}} display="flex" alignItems="center" justifyContent="center" zIndex="3">
          <NavLink to="/"><Image src={logoImage} alt='lmctplus image' maxWidth="184px"/></NavLink>
        </Box>

        <Box position="relative" textAlign="center" zIndex="3">
          <Text as="h1" variant="header-gradient" 
            fontSize={{base: '32px', sm: '40px', md: '48px'}}
            >
            Welcome to LMCT+</Text>
          <Box bg={topLine} left="50%" transform="translateX(-50%) rotate(180deg)" bottom="-2px" h="1px" position="absolute" w="20%" />
        </Box>

        <Flex 
            display="inline-flex"
            textAlign="center"
            gap="8px"
            m="8px auto"
            zIndex="3"
            position="relative"
            alignItems="center">
            <StarsIcon fontSize="24px" />
            <Box borderRadius={{base: '8px', md: '16px'}} p="4px 16px" background="white" zIndex="3">
              <Text as="h2" variant="header-gradient" fontSize={{base: '26px', sm: '32px', md: '40px'}}>Sign up</Text>
            </Box>
            <StarsIcon fontSize="24px" />
        </Flex>   

        <Box position="relative" m="-80px auto -180px auto">
          <Image src={headerImage} alt="LMCT Sign Up" m="0 auto" h={{base: '232px', lg: '370px'}} objectFit="cover" />
          <Box 
            bg={ isDark ? '#00071C' : '#dfe9fd'} 
            zIndex="1" filter="blur(60px)" opacity="0.5" 
            borderRadius="100%" w={{base: '100%'}} 
            top="-60%"
            m="0 auto"
            h="370px" position="absolute" />
          { isDark && <Box bg="rgba(64, 155, 255, 0.82)" 
              zIndex="-1" position="absolute" opacity="0.6" 
              left="30%"
              transform="translate(-50%,0)"
              filter="blur(70px)" h="300px" w="200px" mt="0" 
              top="0"
              borderRadius="full" /> }
          { isDark && <Box bg="rgba(64, 155, 255, 0.82)" 
              zIndex="-1" position="absolute" opacity="0.6" 
              right="30%"
              transform="translate(50%,0)"
              filter="blur(70px)" h="300px" w="200px" mt="0" 
              top="0"
              borderRadius="full" /> }
        </Box>

        <Flex gap="10px" m="38px auto" w={{base: '100%', md: '730px'}} alignItems="center">
          <Card 
            padding="16px" 
            textAlign="left"
            border="2px solid #2ABDFD"
            boxShadow="0 1px 22.4px 0 rgba(9, 155, 255, 0.60)"
            >
            <Text variant="title-lg" fontSize="24px" fontWeight="900" textTransform="uppercase">Membership</Text>
            <Text variant="text-md" mt="20px">
              Unlock the full platform experience.<br /><br />
              Enjoy <span style={spanStyle}>unlimited access</span> to discounts, entries into 
              every giveaway (cars, houses, cash), events, special rewards, 
              and private community access.<br /><br />
              Upgrade now and <span style={spanStyle}>enjoy everything without limits</span> .
            </Text>
            <Button onClick={() => handleType('membership')} variant={ isDark ? 'button-lg-g' : 'button-lg'} mt="24px" leftIcon={<SignUpMembershipIcon fontSize="24px" />}>
              Get Full Access
            </Button>
          </Card> 

          <Card padding="16px" textAlign="left">
            <Text variant="title-lg" fontSize="24px" fontWeight="900" textTransform="uppercase">Free Play</Text>
            <Text variant="text-md" mt="20px">
              Start for free. Upgrade anytime. <br />
              Create a free account and enjoy in-app games. <br />
              Upgrade to unlock all events and exclusive experiences.
            </Text>
            <Button onClick={() => handleType('free')} variant="secondary-inverted-lg" mt="24px" leftIcon={<SignUpGamesIcon fontSize="24px" />}>
              Play for Free
            </Button>
          </Card>   
        </Flex>   
      </Box>
    }
    { step === 2 && <SignUp setStep={setStep} newObject={newObject} setNewObject={setNewObject} onOpenFinish={onOpen} topLine={topLine}/> }
    { step === 3 && 
      <>
        <SignUpFinish 
          newObject={newObject}
          isOpen={isOpen} 
          onOpen={onOpen}
          isDark={isDark} 
          topLine={topLine}
        /> 
      </>
    }
    </>
  );
}

export default SelectSignUp;